import { Dialog } from '@headlessui/react';
import type { NewBillingCycle } from '@weft/types';
import { PLAN_STATUSES } from '@weft/types';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Button } from '../../../components/design/buttons/Button/Button';
import { Input } from '../../../components/design/Input';
import Toggle from '../../../components/design/toggle';
import { useBillingCyclesOverview } from '../../../hooks/requests/use-billing-cycle-overview/use-billing-cycle-overview';
import { NewBillingCycleInitialValues } from '../billing-cycle-consts/billing-cycle-input-initial-values';
import { BillingDayDropdown } from './BillingDayDropdown';
import { CustomersDropdown } from './CustomersDropdown';

type AddBillingCycleDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (NewBillingCycle: NewBillingCycle, onSuccess?: () => void) => void;
};

export const AddEditBillingCycleDialog:React.FC<AddBillingCycleDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const { register, setValue, watch, handleSubmit, reset, formState: { errors } } = useFormContext<NewBillingCycle>();
  const { refetch: refetchBillingCycles } = useBillingCyclesOverview();

  const handleClose = () => {
    onClose();
    reset(NewBillingCycleInitialValues);
  };

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit?.(data, () => {
      refetchBillingCycles().then(() => handleClose());
    });
  });

  const nameRegister = register('name');

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="relative z-50"
    >
      <div
        className="fixed inset-0 bg-black/30"
        aria-hidden="true"
      />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative w-full max-w-[49rem] overflow-y-auto rounded-2xl bg-white">
          <form onSubmit={handleFormSubmit}>
            <div className="px-14 py-10">
              <div className="border-b-line mb-6 flex items-center justify-between border-b pb-6">
                <div className="text-2xl font-medium">System Default Billing Day</div>
                <div className="flex items-center gap-2">
                  <div>Active</div>
                  <Toggle
                    {...register('status')}
                    switchClassName='translate-y-1'
                    checked={watch('status') === PLAN_STATUSES.Active}
                    onToggle={(isActive) => {
                      setValue('status', isActive ? PLAN_STATUSES.Active : PLAN_STATUSES.Inactive);
                    }}
                  />
                </div>
              </div>
              <div className="space-y-10">
                <div className="flex items-center gap-6">
                  <Input
                    {...nameRegister}
                    label='Display Name'
                    placeholder='Text here'
                    wrapperClassName="w-auto"
                    errorMessage={errors[nameRegister.name]?.message}
                  />
                  <BillingDayDropdown/>
                </div>

                <CustomersDropdown/>

                <Button
                  className="h-[42px] px-6 py-2"
                  type="submit"
                >
                  Apply
                </Button>

              </div>

            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
